import styled from "styled-components";

export const PageWrapper = styled.div`
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 20px 64px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-weight: 400;
  line-height: normal;
  margin-top: 40px;

  @media (max-width: 640px) {
    padding: 16px 12px 48px;
  }
`;

export const PageTitle = styled.h1`
  margin: 0 0 16px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 640px) {
    font-size: 22px;
  }
`;

export const IntroText = styled.p`
  margin: 0 0 12px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;
`;

export const OrderedList = styled.ol`
  margin: 0 0 16px;
  padding-left: 22px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;

  li {
    margin: 6px 0;
  }
`;

export const Divider = styled.hr`
  margin: 26px 0;
  border: none;
  height: 1px;
  background: var(--color-line);
`;

export const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
`;

export const SectionText = styled.p`
  margin: 0 0 12px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14.5px;
  font-weight: 500;
  line-height: 1.55;

  strong {
    color: var(--color-text-strong);
    font-weight: 700;
  }
`;

export const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 360px));
  justify-content: center;
  gap: 20px;
  margin: 20px auto 6px;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 320px);
    gap: 14px;
  }
`;

export const ImageCenter = styled.div`
  display: flex;
  justify-content: center;
  margin: 22px 0 6px;
`;

export const StepImage = styled.img`
  display: block;
  width: 100%;
  max-width: 360px;
  height: auto;
  border-radius: 20px;

  filter: drop-shadow(
    0 18px 36px -16px rgba(var(--color-brand-500-rgb), 0.32),
    0 8px 18px -10px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.5)
  );
  object-fit: contain;

  [data-theme="dark"] & {
    filter: drop-shadow(
      0 18px 36px -16px rgba(0, 0, 0, 0.55),
      0 8px 18px -10px rgba(0, 0, 0, 0.4)
    );
  }
`;

export const ImportantNote = styled.div`
  position: relative;
  margin: 16px 0 6px;
  padding: 18px 20px 18px 64px;
  border-radius: 18px;
  border: 1px solid rgba(91, 163, 229, 0.22);
  background: linear-gradient(
    180deg,
    rgba(var(--color-brand-300-rgb), 0.18) 0%,
    rgba(var(--color-brand-200-rgb), 0.32) 100%
  );
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14.5px;
  font-weight: 500;
  line-height: 1.55;
  box-shadow:
    0 8px 20px -10px rgba(var(--color-brand-500-rgb), 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: linear-gradient(
      180deg,
      rgba(var(--color-brand-400-rgb), 0.18) 0%,
      rgba(var(--color-brand-700-rgb), 0.18) 100%
    );
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  }

  &::before {
    content: "!";
    position: absolute;
    left: 16px;
    top: 16px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 100%
    );
    color: #fff;
    font-family: var(--font-family-primary);
    font-size: 18px;
    font-weight: 800;
    line-height: 1;
    box-shadow:
      0 6px 14px rgba(var(--color-brand-600-rgb), 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.5);
  }

  strong {
    display: block;
    margin-bottom: 4px;
    color: var(--color-text-strong);
    font-weight: 800;
    letter-spacing: -0.01em;
  }

  @media (max-width: 640px) {
    padding: 16px 16px 16px 58px;
    font-size: 14px;

    &::before {
      width: 28px;
      height: 28px;
      font-size: 16px;
      left: 14px;
      top: 14px;
    }
  }
`;

export const TipsBox = styled.section`
  margin-top: 32px;
  padding: 22px 24px;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow: var(--shadow-micro);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  @media (max-width: 640px) {
    padding: 18px 18px;
    border-radius: 18px;
  }
`;

export const TipsTitle = styled.h3`
  margin: 0 0 14px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
`;

export const TipItem = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  margin: 10px 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;

  strong {
    color: var(--color-text-strong);
    font-weight: 700;
  }
`;

export const TipNumber = styled.span`
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  color: var(--color-brand-600);
  letter-spacing: 0.04em;
`;
